import React, { useMemo, useState } from 'react';
import { money } from './Weekly/weeklyTableHelpers';

/**
 * A clickable column header that toggles ascending/descending sort and renders the
 * two-chevron indicator, highlighting the active direction in the brand purple.
 *
 * @param {Object} props
 * @param {string} props.label - The column heading text.
 * @param {string} props.sortKey - Dot-path into the row used for sorting (e.g. "snapshot.trips").
 * @param {{key: string, direction: string}} props.sortConfig - The current sort state.
 * @param {Function} props.onSort - Callback invoked with sortKey when the header is clicked.
 * @param {string} [props.align] - Text alignment for the header cell ("left" or "right").
 * @returns {JSX.Element}
 */
const SortableTh = ({ label, sortKey, sortConfig, onSort, align = 'left', width }) => (
  <th
    className={`sticky top-0 z-10 bg-white p-2 cursor-pointer text-${align}`}
    style={width ? { width } : undefined}
    onClick={() => onSort(sortKey)}>
    {label}{' '}
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width="16"
      height="32"
      className="inline-block ml-1">
      <path
        d="M12 5l-5 5h10l-5-5z"
        fill={sortConfig.key === sortKey && sortConfig.direction === 'asc' ? '#745D96' : '#9C9C9C'}
      />
      <path
        d="M12 19l5-5H7l5 5z"
        fill={sortConfig.key === sortKey && sortConfig.direction === 'desc' ? '#745D96' : '#9C9C9C'}
      />
    </svg>
  </th>
);

/**
 * Read-only Driver Overview preview table. Its columns mirror the blue-header columns of the
 * Fares Master Mock CSV (App ID, Driver Name, Fleet, Earnings, Total Trips) so the on-screen view
 * previews exactly what the "Download CSV" button exports. Each row links into the per-driver
 * Driver Payments Detailed View.
 *
 * @param {Object} props
 * @param {Array<Object>} props.data - Driver rows from the driver_summary "driver_table" payload.
 * @param {{from: string, to: string}} props.week - The selected week range, forwarded to the detail link.
 * @returns {JSX.Element}
 */
const DriverPreviewTable = ({ data, week }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });

  const cmp = (a, b) => {
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;

    const aNum = Number(a);
    const bNum = Number(b);
    const bothNumbers = !Number.isNaN(aNum) && !Number.isNaN(bNum);
    if (bothNumbers) return aNum - bNum;

    return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return data;
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    // Split the sort key once here rather than re-splitting it for every comparison operand.
    const path = sortConfig.key.split('.');
    const valueAt = (obj) => path.reduce((acc, key) => (acc?.[key] ?? undefined), obj);
    return [...data].sort((a, b) => dir * cmp(valueAt(a), valueAt(b)));
  }, [data, sortConfig]);

  const filteredData = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return sortedData;
    return sortedData.filter((item) =>
      (item.account_name ?? '').toString().toLowerCase().includes(q)
    );
  }, [sortedData, searchTerm]);

  return (
    <div className="relative w-full">
      <div className="relative flex w-1/4 items-center">
        <input
          type="text"
          placeholder="Search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ paddingTop: '0.44rem', paddingBottom: '0.44rem' }}
          className="w-full pl-4 pr-10 text-gray-800 bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 placeholder-gray-400 placeholder-opacity-100 font-light text-sm"
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          <svg
            className="w-5 h-5 text-gray-700"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            ></path>
          </svg>
        </div>
      </div>

      <table className="table-fixed border-collapse w-full">
        <thead className="sticky top-0 z-20 bg-white">
          <tr className="bg-white border-b">
            <SortableTh label="App ID" sortKey="user_id" sortConfig={sortConfig} onSort={handleSort} width="13%" />
            <SortableTh label="Driver Name" sortKey="account_name" sortConfig={sortConfig} onSort={handleSort} width="19%" />
            <SortableTh label="Fleet" sortKey="fleet_name" sortConfig={sortConfig} onSort={handleSort} width="20%" />
            <SortableTh label="Earnings" sortKey="snapshot.total_corporate_earnings" sortConfig={sortConfig} onSort={handleSort} width="16%" />
            <SortableTh label="Total Trips" sortKey="snapshot.trips" sortConfig={sortConfig} onSort={handleSort} width="27%" />
            <th className="sticky top-0 z-10 bg-white p-2 text-left" style={{ width: '5%' }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {filteredData.map((item) => (
            <tr key={item.account_id} className="border-b hover:bg-gray-50">
              <td className="p-2 text-left font-light">{item.user_id}</td>
              <td className="p-2 text-left font-light truncate">{item.account_name}</td>
              <td className="p-2 text-left font-light truncate">{item.fleet_name}</td>
              <td className="p-2 text-left font-light">{money(item.snapshot?.total_corporate_earnings)}</td>
              <td className="p-2 text-left font-light">{item.snapshot?.trips ?? 0}</td>
              <td className="p-2 text-left">
                <a
                  href={`/payments/drivers/${item.user_id}?start_date_filter=${encodeURIComponent(week?.from || '')}&end_date_filter=${encodeURIComponent(week?.to || '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 bg-gray-700 rounded hover:bg-gray-200 inline-flex">
                  <svg
                    width="20"
                    height="17"
                    viewBox="0 0 20 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M8 7C8 7.53043 8.21071 8.03914 8.58579 8.41421C8.96086 8.78929 9.46957 9 10 9C10.5304 9 11.0391 8.78929 11.4142 8.41421C11.7893 8.03914 12 7.53043 12 7C12 6.46957 11.7893 5.96086 11.4142 5.58579C11.0391 5.21071 10.5304 5 10 5C9.46957 5 8.96086 5.21071 8.58579 5.58579C8.21071 5.96086 8 6.46957 8 7Z"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M19 7C16.6 11 13.6 13 10 13C6.4 13 3.4 11 1 7C3.4 3 6.4 1 10 1C13.6 1 16.6 3 19 7Z"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default DriverPreviewTable;
